import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { ApiError } from '../../lib/api.ts';
import {
  isImportable,
  useCreateSpreadsheet,
  useImportSpreadsheet,
  useSheetTemplates,
} from '../../lib/sheet-queries.ts';
import { SavePicker } from './SavePicker.tsx';
import { useFileUpload } from '../drive/useFileUpload.ts';
import { settleName } from '../drive/settleName.ts';
import { createElement } from 'react';

/**
 * Making a sheet, without the button around it.
 *
 * Pulled out of NewSheetButton so the Drive's single New menu and the
 * dashboard's split button are the same act rather than two implementations
 * that drift. The flows are the interesting part — where a sheet is filed,
 * what happens to one workbook versus twelve — and there should be one of
 * each.
 */

export interface SheetCreation {
  templates: ReturnType<typeof useSheetTemplates>;
  /** Blank, or from a template id. Opens the save picker. */
  createFrom(options: { title: string; suggested: string; template?: string }): void;
  importFiles(files: readonly File[]): Promise<void>;
}

export function useSheetCreation(
  folderId: string | null,
  onError: (message: string) => void,
): SheetCreation {
  const navigate = useNavigate();
  const dialog = useDialog();
  const toast = useToast();
  const templates = useSheetTemplates();
  const create = useCreateSpreadsheet();
  const importSheet = useImportSpreadsheet();
  const startUpload = useFileUpload(folderId);

  const createFrom = useCallback(
    (options: { title: string; suggested: string; template?: string }) => {
      dialog.open((close) =>
        // createElement rather than JSX: this is a .ts module, and making it
        // .tsx to render one element would be the tail wagging the dog.
        createElement(SavePicker, {
          title: options.title,
          initialName: options.suggested,
          initialFolderId: folderId,
          confirmLabel: 'Create',
          onClose: close,
          onConfirm: async (target: { name: string; folderId: string | null }) => {
            try {
              // The same question an upload asks, rather than a refusal from
              // the server that leaves the user guessing at a free name.
              const settled = await settleName(dialog, 'sheet', target.name, target.folderId);
              if (!settled) return;

              const created = await create.mutateAsync({
                name: target.name,
                folderId: target.folderId,
                onConflict: settled.policy,
                ...(options.template && options.template !== 'blank'
                  ? { template: options.template }
                  : {}),
              });
              toast.success(`Created "${created.name}"`);
              navigate(`/sheets/${created.id}`);
            } catch (caught) {
              const message =
                caught instanceof ApiError ? caught.message : 'Could not create the sheet.';
              toast.error('Could not create the sheet', message);
              onError(message);
            }
          },
        }),
      );
    },
    [dialog, folderId, create, toast, navigate, onError],
  );

  const importOne = useCallback(
    (file: File) => {
      dialog.open((close) =>
        createElement(SavePicker, {
          title: `Import "${file.name}"`,
          initialName: file.name.replace(/\.[^.]+$/, ''),
          initialFolderId: folderId,
          confirmLabel: 'Import',
          onClose: close,
          onConfirm: async (target: { name: string; folderId: string | null }) => {
            try {
              const created = await importSheet.mutateAsync({ file, folderId: target.folderId });
              toast.success(`Imported "${created.name}"`, 'Formulas and formatting came across.');
              navigate(`/sheets/${created.id}`);
            } catch (caught) {
              const message =
                caught instanceof ApiError ? caught.message : 'Could not import that workbook.';
              toast.error('Could not import', message);
              onError(message);
            }
          },
        }),
      );
    },
    [dialog, folderId, importSheet, toast, navigate, onError],
  );

  /**
   * One is worth a dialog: it can be named and filed in the same breath.
   * Several is not — being asked to name and place each of twelve files in
   * turn is worse than useless — so a batch goes through the ordinary upload
   * queue into the current folder, keeping each file's own name. The server
   * converts a workbook to a sheet on arrival either way.
   */
  const importFiles = useCallback(
    async (chosen: readonly File[]) => {
      if (chosen.length === 0) return;

      const usable = chosen.filter((file) => isImportable(file.name));
      if (usable.length === 0) {
        toast.error('That is not a spreadsheet', 'Import an .xlsx, .xls, .csv or .ods file.');
        return;
      }
      if (usable.length < chosen.length) {
        toast.error(
          'Some files were not spreadsheets',
          `Importing ${usable.length} of ${chosen.length}. The rest are not .xlsx, .xls, .csv or .ods.`,
        );
      }

      if (usable.length > 1) {
        await startUpload(usable);
        return;
      }

      importOne(usable[0] as File);
    },
    [toast, startUpload, importOne],
  );

  return { templates, createFrom, importFiles };
}
